import * as React from 'react';
import { useEffect, useState } from 'react';

import {
  defaultWrapLinesPreference,
  i18nStrings,
  mediumPageSizePreference,
  paginationLabels,
} from 'src/commons/tables';

import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Pagination,
  PropertyFilter,
  Table,
} from '@amzn/awsui-components-react-v3';

import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState } from 'src/commons/EmptyState';

export interface ReviewAgreementResourcesTableProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  setSelectedAgreementResources: any;
  selectedAgreementResources: any;
  allAgreementResources: any;
  loadingReviews: any;
  columnDefinitions: any;
  agreementDifferences: any;
}

export const ReviewAgreementResourcesTable = (props: ReviewAgreementResourcesTableProps) => {
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 50,
  });

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount, actions } = useCollection(
    props.allAgreementResources,
    {
      filtering: {
        empty: <EmptyState title='No resources' subtitle='No resources.' />,
        noMatch: <EmptyState title='No matches' subtitle='We can’t find a match.' />,
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {
        defaultState: {
          sortingColumn: {
            sortingField: 'name',
          },
        },
      },
      selection: {},
      propertyFiltering: {
        filteringProperties: [
          {
            propertyLabel: 'Submitted by',
            key: 'reviewedBy',
            groupValuesLabel: 'Submitted by',
          },
          {
            propertyLabel: 'Action',
            key: 'reviewAction',
            groupValuesLabel: 'Actions',
          },
          {
            propertyLabel: 'Comment',
            key: 'reviewComment',
            groupValuesLabel: 'Comments',
          },
          {
            propertyLabel: 'Agreement override',
            key: 'overrideAgreementsReason',
            groupValuesLabel: 'Agreement override',
          },
          {
            propertyLabel: 'Review season',
            key: 'seasonId',
            groupValuesLabel: 'Review seasons',
          },
          {
            propertyLabel: 'Review season',
            key: 'seasonId',
            groupValuesLabel: 'Review seasons',
          },
          {
            propertyLabel: 'Resource namespace',
            key: 'namespace',
            groupValuesLabel: 'Resource namespaces',
          },
          {
            propertyLabel: 'Resource type',
            key: 'resourceType',
            groupValuesLabel: 'Resource types',
          },
          {
            propertyLabel: 'Resource ID',
            key: 'resourceId',
            groupValuesLabel: 'Resource IDs',
          },
        ],
      },
    },
  );

  useEffect(() => {
    actions.setSelectedItems(items);
  }, [props.agreementDifferences]);

  useEffect(() => {
    const { selectedItems } = collectionProps;
    props.setSelectedAgreementResources(selectedItems);
  }, [collectionProps.selectedItems]);

  return (
    <>
      <Table
        {...collectionProps}
        loadingText='Loading reviews'
        loading={props.loadingReviews}
        columnDefinitions={props.columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        selectionType='multi'
        header={
          <Header counter={`(${collectionProps.selectedItems.length}/${props.allAgreementResources?.length})`}>
            Associated reviews
          </Header>
        }
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            disabled={props.loadingReviews}
            i18nStrings={i18nStrings}
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
        empty={<EmptyState title={'No resources'} subtitle='No resources' />}
        pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={mediumPageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
      />
    </>
  );
};
